/**
 * UserSecurityTab — F12.M Etap D Part 2 (Users fullscreen, Etap 1.4).
 *
 * Zakładka „Bezpieczeństwo konta" — wszystkie akcje na koncie usera + status 2FA.
 *
 * Akcje (per status / userType):
 *  - Wyślij link resetu hasła → POST /api/users/:id/trigger-password-reset (status != invited)
 *  - Ustaw hasło tymczasowe → TenantUserSetTempPasswordModal (status != invited)
 *  - Zmień e-mail logowania / username → TenantUserChangeIdentifierModal
 *  - Wyloguj ze wszystkich sesji → POST /api/users/:id/logout-all
 *  - Zawieś / Aktywuj → POST /api/users/:id/suspend|activate
 *  - Wyślij ponownie zaproszenie → POST /api/users/:id/resend-invite (status == invited)
 *  - Przywróć → POST /api/users/:id/restore (gdy soft-deleted)
 *  - Usuń → DELETE /api/users/:id
 *
 * 2FA: tylko badge w nagłówku — admin NIE może wyłączyć 2FA innego usera
 * (świadoma decyzja: 2FA chroni przed kradzieżą konta przez admina; user musi
 * sam wyłączyć przez Profil albo skontaktować się z supportem).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  KeyRound,
  LogOut,
  Mail,
  RotateCcw,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  UserCog,
} from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { TenantUserChangeIdentifierModal } from '@/components/TenantUserChangeIdentifierModal';
import { TenantUserSetTempPasswordModal } from '@/components/TenantUserSetTempPasswordModal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { USERS_LIST_KEY } from '@/lib/use-permissions';
import {
  USER_DETAIL_KEY,
  type UserDetail,
  type UserRestoreResponse,
} from '@/lib/use-tenant-people';

import { showUserRestoredToasts } from './restore-toasts';

interface Props {
  user: UserDetail;
  /**
   * Konto zablokowane na czas zadania przycisku SQL — „Usuń" i „Przywróć" nieaktywne (serwer odrzuca obie
   * operacje 409 `RECORD_LOCKED_BY_RUN`, dopóki zadanie trwa). Pozostałe akcje konta nie są blokowane.
   */
  recordLocked?: boolean;
  /**
   * Obsługa błędu blokady zadaniem: gdy odpowiedź to `RECORD_LOCKED_BY_RUN`, pokazuje komunikat z opisem
   * zadania, odświeża stan zadań strony i zwraca `true`; inaczej `false` (zwykły komunikat błędu akcji).
   */
  onLockedError?: (err: unknown) => boolean;
}

type ConfirmAction =
  | { kind: 'triggerReset' }
  | { kind: 'logoutAll' }
  | { kind: 'activate' }
  | { kind: 'resendInvite' }
  | { kind: 'delete' }
  | { kind: 'restore' };

export function UserSecurityTab({ user, recordLocked = false, onLockedError }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [showSuspend, setShowSuspend] = useState(false);
  const [showChangeIdentifier, setShowChangeIdentifier] = useState(false);
  const [showSetTempPassword, setShowSetTempPassword] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  const isDeleted = user.gcrecord != null;
  const isHuman = user.userType === 'human';

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: USER_DETAIL_KEY(user.id) });
    queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
  }

  // Mutacje (każda akcja osobna, dla czytelnego onSuccess toast)
  const triggerResetM = useMutation({
    mutationFn: () => api.post(`/users/${user.id}/trigger-password-reset`),
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.users.toast.passwordResetSent') });
    },
    onError: (err) => handleErr(err, t, toast),
  });

  const logoutAllM = useMutation({
    mutationFn: () => api.post(`/users/${user.id}/logout-all`),
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.users.toast.logoutAllDone') });
    },
    onError: (err) => handleErr(err, t, toast),
  });

  const suspendM = useMutation({
    mutationFn: () => api.post(`/users/${user.id}/suspend`, { reason: suspendReason || undefined }),
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.users.toast.suspended') });
      setShowSuspend(false);
      setSuspendReason('');
    },
    onError: (err) => handleErr(err, t, toast),
  });

  const activateM = useMutation({
    mutationFn: () => api.post(`/users/${user.id}/activate`),
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.users.toast.activated') });
    },
    onError: (err) => handleErr(err, t, toast),
  });

  const resendInviteM = useMutation({
    mutationFn: () => api.post(`/users/${user.id}/resend-invite`),
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.users.toast.resendInviteSent') });
    },
    onError: (err) => handleErr(err, t, toast),
  });

  // Usunięcie i przywrócenie to trasy blokowane na czas zadania przycisku SQL: odrzucenie 409
  // `RECORD_LOCKED_BY_RUN` dostaje komunikat z opisem zadania (od strony), a nie ogólny błąd akcji.
  const deleteM = useMutation({
    mutationFn: () => api.delete(`/users/${user.id}`),
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.users.toast.deleted') });
    },
    onError: (err) => {
      if (onLockedError?.(err) === true) return;
      handleErr(err, t, toast);
    },
  });

  const restoreM = useMutation({
    mutationFn: () => api.post<UserRestoreResponse>(`/users/${user.id}/restore`),
    onSuccess: (res) => {
      invalidate();
      showUserRestoredToasts(toast, t, res.data);
    },
    onError: (err) => {
      if (onLockedError?.(err) === true) return;
      handleErr(err, t, toast);
    },
  });

  function getConfirmConfig(action: ConfirmAction): {
    title: string;
    message: string;
    variant: 'danger' | 'primary';
    confirmLabel: string;
  } {
    switch (action.kind) {
      case 'triggerReset':
        return {
          title: t('tenant.users.detail.confirm.triggerReset.title'),
          message: t('tenant.users.detail.confirm.triggerReset.message', { email: user.email }),
          variant: 'primary',
          confirmLabel: t('tenant.users.actions.triggerPasswordReset'),
        };
      case 'logoutAll':
        return {
          title: t('tenant.users.detail.confirm.logoutAll.title'),
          message: t('tenant.users.detail.confirm.logoutAll.message'),
          variant: 'primary',
          confirmLabel: t('tenant.users.actions.logoutAll'),
        };
      case 'activate':
        return {
          title: t('tenant.users.detail.confirm.activate.title'),
          message: t('tenant.users.detail.confirm.activate.message'),
          variant: 'primary',
          confirmLabel: t('tenant.users.actions.activate'),
        };
      case 'resendInvite':
        return {
          title: t('tenant.users.detail.confirm.resendInvite.title'),
          message: t('tenant.users.detail.confirm.resendInvite.message', { email: user.email }),
          variant: 'primary',
          confirmLabel: t('tenant.users.actions.resendInvite'),
        };
      case 'delete':
        return {
          title: t('tenant.users.detail.confirm.delete.title'),
          message: t('tenant.users.detail.confirm.delete.message'),
          variant: 'danger',
          confirmLabel: t('tenant.users.actions.delete'),
        };
      case 'restore':
        return {
          title: t('tenant.users.detail.confirm.restore.title'),
          message: t('tenant.users.detail.confirm.restore.message'),
          variant: 'primary',
          confirmLabel: t('tenant.users.actions.restore'),
        };
    }
  }

  function runAction(action: ConfirmAction) {
    setConfirmAction(null);
    switch (action.kind) {
      case 'triggerReset':
        triggerResetM.mutate();
        break;
      case 'logoutAll':
        logoutAllM.mutate();
        break;
      case 'activate':
        activateM.mutate();
        break;
      case 'resendInvite':
        resendInviteM.mutate();
        break;
      case 'delete':
        deleteM.mutate();
        break;
      case 'restore':
        restoreM.mutate();
        break;
    }
  }

  return (
    <div className="space-y-4 p-4" data-testid="user-security-tab">
      {/* Karta info: 2FA + Status konta */}
      <div className="rounded-lg border border-border-secondary bg-bg-primary p-5">
        <div className="mb-3 flex items-center gap-2 text-xs font-medium text-text-secondary">
          <KeyRound size={12} aria-hidden="true" />
          {t('tenant.users.detail.securityInfo')}
        </div>
        <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
          <div className="flex items-center justify-between">
            <span className="text-text-tertiary">{t('tenant.users.detail.twoFA')}</span>
            <Badge color={user.totpEnabled ? 'green' : 'gray'}>
              {t(
                user.totpEnabled
                  ? 'tenant.users.detail.twoFAEnabled'
                  : 'tenant.users.detail.twoFADisabled',
              )}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-text-tertiary">{t('tenant.users.detail.statusLabel')}</span>
            <Badge
              color={
                user.status === 'active' ? 'green' : user.status === 'suspended' ? 'red' : 'amber'
              }
            >
              {t(`tenant.users.status.${user.status}`)}
            </Badge>
          </div>
        </div>
        {!user.totpEnabled && (
          <p className="mt-3 text-xs text-text-tertiary">
            {t('tenant.users.detail.twoFAResetHint')}
          </p>
        )}
      </div>

      {/* Akcje konta */}
      {isHuman && !isDeleted && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Button
            variant="secondary"
            leftIcon={<Mail size={14} aria-hidden="true" />}
            onClick={() => setConfirmAction({ kind: 'triggerReset' })}
            disabled={user.status === 'invited' || triggerResetM.isPending}
            title={user.status === 'invited' ? t('tenant.users.detail.disabledInvited') : undefined}
            data-testid="user-security-trigger-reset"
          >
            {t('tenant.users.actions.triggerPasswordReset')}
          </Button>
          <Button
            variant="secondary"
            leftIcon={<KeyRound size={14} aria-hidden="true" />}
            onClick={() => setShowSetTempPassword(true)}
            disabled={user.status === 'invited'}
            title={user.status === 'invited' ? t('tenant.users.detail.disabledInvited') : undefined}
            data-testid="user-security-set-temp-password"
          >
            {t('tenant.users.actions.setTempPassword')}
          </Button>
          <Button
            variant="secondary"
            leftIcon={<UserCog size={14} aria-hidden="true" />}
            onClick={() => setShowChangeIdentifier(true)}
            data-testid="user-security-change-identifier"
          >
            {t('tenant.users.actions.changeIdentifier')}
          </Button>
          <Button
            variant="secondary"
            leftIcon={<LogOut size={14} aria-hidden="true" />}
            onClick={() => setConfirmAction({ kind: 'logoutAll' })}
            disabled={logoutAllM.isPending}
            data-testid="user-security-logout-all"
          >
            {t('tenant.users.actions.logoutAll')}
          </Button>
          {user.status === 'suspended' ? (
            <Button
              variant="secondary"
              leftIcon={<ShieldCheck size={14} aria-hidden="true" />}
              onClick={() => setConfirmAction({ kind: 'activate' })}
              disabled={activateM.isPending}
              data-testid="user-security-activate"
            >
              {t('tenant.users.actions.activate')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              leftIcon={<ShieldAlert size={14} aria-hidden="true" />}
              onClick={() => setShowSuspend(true)}
              disabled={suspendM.isPending}
              data-testid="user-security-suspend"
            >
              {t('tenant.users.actions.suspend')}
            </Button>
          )}
          {user.status === 'invited' && (
            <Button
              variant="secondary"
              leftIcon={<Mail size={14} aria-hidden="true" />}
              onClick={() => setConfirmAction({ kind: 'resendInvite' })}
              disabled={resendInviteM.isPending}
              data-testid="user-security-resend-invite"
            >
              {t('tenant.users.actions.resendInvite')}
            </Button>
          )}
        </div>
      )}

      {/* Usunięcie / przywrócenie konta */}
      {isHuman && (
        <div className="mt-4">
          {isDeleted ? (
            <Button
              variant="secondary"
              leftIcon={<RotateCcw size={14} aria-hidden="true" />}
              onClick={() => setConfirmAction({ kind: 'restore' })}
              disabled={restoreM.isPending || recordLocked}
              data-testid="user-security-restore"
            >
              {t('tenant.users.actions.restore')}
            </Button>
          ) : (
            <Button
              variant="danger"
              leftIcon={<Trash2 size={14} aria-hidden="true" />}
              onClick={() => setConfirmAction({ kind: 'delete' })}
              disabled={deleteM.isPending || recordLocked}
              data-testid="user-security-delete"
            >
              {t('tenant.users.actions.deleteUser')}
            </Button>
          )}
        </div>
      )}

      {/* Modal: Suspend z polem reason (reuse istniejących i18n keys z tenant.users.suspendModal.*) */}
      <Modal
        open={showSuspend}
        onClose={() => setShowSuspend(false)}
        size="md"
        title={t('tenant.users.suspendModal.title')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowSuspend(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => suspendM.mutate()}
              disabled={suspendM.isPending}
              data-testid="user-security-suspend-confirm"
            >
              {t('tenant.users.suspendModal.confirm')}
            </Button>
          </div>
        }
      >
        <p className="mb-3 text-sm">
          {t('tenant.users.suspendModal.subtitle', {
            name: user.displayName ?? user.email,
          })}
        </p>
        <Input
          label={t('tenant.users.suspendModal.reasonLabel')}
          value={suspendReason}
          onChange={(e) => setSuspendReason(e.target.value)}
          placeholder={t('tenant.users.suspendModal.reasonHint')}
          data-testid="user-security-suspend-reason"
        />
      </Modal>

      {/* Modal: Change identifier (existing component) */}
      <TenantUserChangeIdentifierModal
        open={showChangeIdentifier}
        onClose={() => setShowChangeIdentifier(false)}
        user={{
          id: user.id,
          email: user.email,
          username: null, // UserDetail nie ma username — backend wczyta sam
          displayName: user.displayName,
        }}
        onSuccess={() => {
          invalidate();
          setShowChangeIdentifier(false);
        }}
      />

      {/* Modal: Set temporary password (existing component — sam zamyka się po sukcesie) */}
      <TenantUserSetTempPasswordModal
        open={showSetTempPassword}
        onClose={() => {
          invalidate();
          setShowSetTempPassword(false);
        }}
        user={{ id: user.id, email: user.email, displayName: user.displayName }}
      />

      {/* Generic confirm dialog */}
      <ConfirmDialog
        open={confirmAction !== null}
        title={confirmAction ? getConfirmConfig(confirmAction).title : ''}
        message={confirmAction ? getConfirmConfig(confirmAction).message : ''}
        variant={confirmAction ? getConfirmConfig(confirmAction).variant : 'danger'}
        confirmLabel={confirmAction ? getConfirmConfig(confirmAction).confirmLabel : undefined}
        onConfirm={() => confirmAction && runAction(confirmAction)}
        onClose={() => setConfirmAction(null)}
      />
    </div>
  );
}

function handleErr(
  err: unknown,
  t: ReturnType<typeof useTranslation>['t'],
  toast: ReturnType<typeof useToast>,
): void {
  if (isAxiosError(err)) {
    const code = err.response?.data?.error as string | undefined;
    if (code) {
      const msg = t(`tenant.users.errors.${code}`, { defaultValue: '' });
      if (msg) {
        toast.show({ variant: 'error', title: msg });
        return;
      }
    }
  }
  toast.show({ variant: 'error', title: t('tenant.users.errors.generic') });
}
